import React, { useEffect, useState } from "react";
import { FiArrowLeft, FiCalendar, FiSearch, FiX } from "react-icons/fi";
import { PaginationControl } from "react-bootstrap-pagination-control";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { getAuth } from "../../services/localStorage";
import { API_ENDPOINT } from "../../services/api";
import ConfirmDialog from "../ConfirmDialog";
import Labels from "../../services/apiLabels";
import {
  authHeaders,
  errorText,
  isApiSuccess,
  listFromPayload,
  officeId,
  readJson,
} from "../Offices/officeHelpers";
import { officeLabel } from "../InspectionUsers/inspectionUserHelpers";
import {
  applyAppointmentStatus,
  appointmentDate,
  appointmentId,
  appointmentTime,
  clientName,
  defaultAppointmentDateRange,
  formatAppointmentTime,
  parseAppointmentsPage,
  trackingNumber,
  vehicleReg,
  vehicleTitle,
} from "./appointmentHelpers";
import OfficeAppointmentDetail from "./OfficeAppointmentDetail";
import AppointmentStatusBadge from "./AppointmentStatusBadge";
import AppointmentActionsMenu from "./AppointmentActionsMenu";
import "../Offices/offices.css";

const PAGE_SIZE_OPTIONS = [5, 10, 20];
const OFFICES_PATH = "/dealers/dashbord/offices";

function OfficeAppointmentList() {
  const history = useHistory();
  const location = useLocation();
  const { officeId: officeIdParam } = useParams();
  const auth = getAuth();

  const [office, setOffice] = useState(location.state?.office || null);
  const [appointments, setAppointments] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState({
    type: location.state?.notice ? "success" : "",
    text: location.state?.notice || "",
  });
  const defaultRange = defaultAppointmentDateRange();
  const [startingDate, setStartingDate] = useState(defaultRange.starting_date);
  const [endingDate, setEndingDate] = useState(defaultRange.ending_date);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    if (!auth?.token || !officeIdParam) return undefined;
    if (
      location.state?.office &&
      String(officeId(location.state.office)) === String(officeIdParam)
    ) {
      setOffice(location.state.office);
      return undefined;
    }
    let cancelled = false;
    fetch(API_ENDPOINT(Labels.office_listing), {
      headers: authHeaders(auth.token),
    })
      .then((response) =>
        readJson(response).then((json) => ({ response, json }))
      )
      .then(({ response, json }) => {
        if (cancelled || !isApiSuccess(response, json)) return;
        const match = listFromPayload(json).find(
          (item) => String(officeId(item)) === String(officeIdParam)
        );
        if (match) setOffice(match);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [auth?.token, officeIdParam, location.state]);

  const loadAppointments = async () => {
    if (!auth?.token || !officeIdParam) {
      setLoading(false);
      setAppointments([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
      });
      if (startingDate) params.set("starting_date", startingDate);
      if (endingDate) params.set("ending_date", endingDate);
      if (search.trim()) params.set("search", search.trim());
      const response = await fetch(
        `${API_ENDPOINT(Labels.office_appointments_listing)}${officeIdParam}/?${params.toString()}`,
        { headers: authHeaders(auth.token) }
      );
      const json = await readJson(response);
      if (!isApiSuccess(response, json)) {
        throw new Error("load failed");
      }
      const parsed = parseAppointmentsPage(json);
      setAppointments(parsed.items);
      setTotal(parsed.total);
    } catch {
      setAppointments([]);
      setTotal(0);
      setBanner((prev) =>
        prev.type === "success"
          ? prev
          : {
              type: "error",
              text: "Could not load appointments. Please try again.",
            }
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [officeIdParam, page, pageSize, startingDate, endingDate, search]);

  useEffect(() => {
    setPage(1);
  }, [startingDate, endingDate, pageSize, search]);

  const pageCount = Math.max(1, Math.ceil(total / pageSize) || 1);
  const safePage = Math.min(page, pageCount);
  const pageStart = total ? (safePage - 1) * pageSize : 0;
  const pageEnd = Math.min(pageStart + appointments.length, total);

  const confirmDelete = async () => {
    const id = appointmentId(deleteTarget);
    if (!id || !auth?.token) return;
    setDeleting(true);
    try {
      const response = await fetch(
        API_ENDPOINT(`${Labels.delete_office_appointment}${id}/`),
        { method: "DELETE", headers: authHeaders(auth.token) }
      );
      const json = await readJson(response);
      if (!isApiSuccess(response, json) && response.status !== 204) {
        throw new Error(errorText(json, "Could not delete this appointment."));
      }
      setDeleteTarget(null);
      setBanner({ type: "success", text: "Appointment removed." });
      await loadAppointments();
    } catch (err) {
      setBanner({
        type: "error",
        text: err?.message || "Could not delete this appointment.",
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleStatusUpdated = (item, status) => {
    const id = appointmentId(item);
    setAppointments((prev) =>
      prev.map((row) =>
        String(appointmentId(row)) === String(id)
          ? applyAppointmentStatus(row, status)
          : row
      )
    );
    setViewing((current) =>
      current && String(appointmentId(current)) === String(id)
        ? applyAppointmentStatus(current, status)
        : current
    );
    setBanner({
      type: "success",
      text:
        status === "approved"
          ? "Appointment approved."
          : "Appointment rejected.",
    });
  };

  return (
    <div className="dd-section dd-offices">
      <div className="dd-arb-intro">
        <span className="dd-arb-intro__badge" aria-hidden>
          <FiCalendar size={18} />
        </span>
        <div>
          <h2>Appointments</h2>
          <p>Bookings for {officeLabel(office)}.</p>
        </div>
      </div>

      {banner.text ? (
        <p
          className={
            banner.type === "success"
              ? "dd-hours-alert dd-hours-alert--success"
              : "dd-hours-alert dd-hours-alert--error"
          }
          role="status"
        >
          {banner.text}
        </p>
      ) : null}

      <div className="dd-toolbar">
        <label className="dd-toolbar__search">
          <FiSearch size={16} aria-hidden />
          <input
            type="search"
            placeholder="Search mileage, registration, brand, or model"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search ? (
            <button
              type="button"
              className="dd-toolbar__clear-input"
              onClick={() => setSearch("")}
              aria-label="Clear search"
            >
              <FiX size={14} />
            </button>
          ) : null}
        </label>
        <label className="dd-toolbar__field">
          <span>From</span>
          <input
            type="date"
            value={startingDate}
            max={endingDate || undefined}
            onChange={(e) => setStartingDate(e.target.value)}
          />
        </label>
        <label className="dd-toolbar__field">
          <span>To</span>
          <input
            type="date"
            value={endingDate}
            min={startingDate || undefined}
            onChange={(e) => setEndingDate(e.target.value)}
          />
        </label>
        <label className="dd-toolbar__field">
          <span>Per page</span>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
        <label className="dd-toolbar__field">
          <span>Offices</span>
          <button
            type="button"
            className="dd-btn dd-btn--ghost"
            onClick={() => history.push(OFFICES_PATH)}
          >
            <FiArrowLeft size={16} aria-hidden />
            Back
          </button>
        </label>
      </div>

      <div className="dd-toolbar__meta">
        {total ? (
          <span>
            Showing {pageStart + 1}–{pageEnd} of {total}
          </span>
        ) : (
          <span>No appointments to show</span>
        )}
      </div>

      {loading ? (
        <p className="dd-muted">Loading appointments…</p>
      ) : !appointments.length ? (
        <div className="dd-empty">
          <strong>No appointments yet</strong>
          <p>No bookings in this date range.</p>
        </div>
      ) : (
        <>
          <div className="dd-table-wrap">
            <table className="dd-table">
              <thead>
                <tr>
                  <th scope="col">Client</th>
                  <th scope="col">Vehicle</th>
                  <th scope="col">Registration</th>
                  <th scope="col">Date</th>
                  <th scope="col">Time</th>
                  <th scope="col">Status</th>
                  <th scope="col">Tracking</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {appointments.map((item, index) => {
                  const id = appointmentId(item);
                  return (
                    <tr
                      key={id || `appt-${index}`}
                      className="dd-table__click-row"
                      onClick={() => setViewing(item)}
                    >
                      <td>{clientName(item)}</td>
                      <td>
                        <strong>{vehicleTitle(item)}</strong>
                        <div className="dd-muted">
                          {item?.evaluation?.variant_name || "—"}
                        </div>
                      </td>
                      <td>{vehicleReg(item)}</td>
                      <td>{appointmentDate(item)}</td>
                      <td>{formatAppointmentTime(appointmentTime(item))}</td>
                      <td>
                        <AppointmentStatusBadge item={item} />
                      </td>
                      <td>{trackingNumber(item)}</td>
                      <td>
                        <AppointmentActionsMenu
                          item={item}
                          token={auth?.token}
                          onStatusUpdated={handleStatusUpdated}
                          onDelete={setDeleteTarget}
                          onError={(text) =>
                            setBanner({ type: "error", text })
                          }
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {total > pageSize ? (
            <div className="dd-pagination">
              <PaginationControl
                page={safePage}
                between={3}
                total={total}
                limit={pageSize}
                changePage={setPage}
                ellipsis={1}
              />
            </div>
          ) : null}
        </>
      )}

      <OfficeAppointmentDetail
        item={viewing}
        onClose={() => setViewing(null)}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete this appointment?"
        message={
          deleteTarget
            ? `Remove ${clientName(deleteTarget)} · ${vehicleTitle(
                deleteTarget
              )} on ${appointmentDate(deleteTarget)} at ${formatAppointmentTime(
                appointmentTime(deleteTarget)
              )}? This cannot be undone.`
            : ""
        }
        confirmLabel="Delete appointment"
        confirming={deleting}
        onCancel={() => (!deleting ? setDeleteTarget(null) : null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}

export default OfficeAppointmentList;
